import getTimezoneOffset from './getTimezoneOffset';

/**
 * Converts a Norwegian date string (YYYY-MM-DD HH:mm) in Europe/Oslo time or an ISO 8601 string (2024-01-01T00:00:00Z) to a UTC Date object.
 *
 * @param {string} dateString - The date string in 'YYYY-MM-DD HH:mm' (Europe/Oslo) or ISO 8601 format.
 * @returns {Date} A JavaScript Date object representing the equivalent UTC time.
 */
function getDateFromNorwegianDateString(dateString: string): Date | null {
    if (isIso8601(dateString)) {
        // Convert ISO 8601 string directly to YYYY-MM-DD HH:mm format
        const [date, time] = dateString.split('T');
        // Remove seconds and Z
        const hhmm = time.split(':');
        const newDateString = `${date} ${hhmm[0]}:${hhmm[1]}`;
        return parseNorwegianLocal(newDateString);
    }

    // Parse as Norwegian local time (Europe/Oslo)
    return parseNorwegianLocal(dateString);
}

/**
 * Checks if a string is in ISO 8601 format (e.g., 2024-01-01T00:00:00Z)
 */
function isIso8601(str: string): boolean {
    // Simple check for T and Z, and valid date pattern
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(str);
}

/**
 * Parses a Norwegian date string in 'YYYY-MM-DD HH:mm' format as Europe/Oslo local time and returns a UTC Date.
 */
function parseNorwegianLocal(norwegianDateString: string): Date | null {
    if (!norwegianDateString) {
        return null;
    }
    const [datePart, timePart] = norwegianDateString.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    // Create a Date object as if it's in Europe/Oslo local time
    const localDate = new Date(year, month - 1, day, hour, minute);
    // Get the offset for Europe/Oslo at this date (in hours)
    const offset = getTimezoneOffset('Europe/Oslo', localDate);
    // Convert local time to UTC by subtracting the offset
    localDate.setHours(localDate.getHours() - offset);
    return new Date(
        Date.UTC(
            localDate.getFullYear(),
            localDate.getMonth(),
            localDate.getDate(),
            localDate.getHours(),
            localDate.getMinutes(),
            localDate.getSeconds(),
            localDate.getMilliseconds()
        )
    );
}

export default getDateFromNorwegianDateString;
